<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import Icon from '../ui/Icon.vue';
import LiveValue from '../ui/LiveValue.vue';
import { useParty, type PartyRowState } from '../../stores/party';
import { useSession } from '../../stores/session';
import { formatSilver } from '../../lib/format';

/**
 * The site's constant: while you are in a party, its live figures stay on
 * screen on every route. The party page itself is the full board, so the strip
 * steps aside there instead of repeating it.
 */
const route = useRoute();
const party = useParty();
const session = useSession();

const visible = computed(() => session.isSignedIn.value && party.party.value !== null && route.path !== '/party');
const dotClass: Record<PartyRowState, string> = {
  grinding: 'bg-success',
  paused: 'bg-yellow',
  stale: 'bg-warning',
  idle: 'bg-500',
};
const statusLabel = computed(() => {
  if (party.liveStatus.value === 'live') return `${party.totals.value.grinding} of ${party.rows.value.length} grinding`;
  if (party.liveStatus.value === 'reconnecting') return 'Reconnecting to live updates';
  return 'Connecting to live updates';
});
</script>

<template>
  <section
    v-if="visible"
    class="sticky top-[102px] z-20 flex h-auto items-stretch gap-4 border-b border-app bg-card px-4 py-2 lg:top-14 lg:h-16 lg:gap-6 lg:px-6 lg:py-0"
    aria-label="Live party"
  >
    <div class="flex shrink-0 flex-col justify-center">
      <span class="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-darkest">
        <Icon name="signal" class="size-3.5" :class="party.liveStatus.value === 'live' ? 'text-success' : 'text-warning'" />
        Party
      </span>
      <span class="mt-0.5 hidden text-[12px] text-darker lg:block">{{ statusLabel }}</span>
    </div>

    <!-- Desktop: one column per member, separated by hairlines, never pills. -->
    <ul class="hidden min-w-0 flex-1 items-center gap-5 overflow-x-auto lg:flex">
      <li
        v-for="row in party.rows.value"
        :key="row.user.id"
        class="flex shrink-0 flex-col justify-center border-l border-app pl-5 first:border-l-0 first:pl-0"
      >
        <span class="flex items-center gap-1.5 text-[13px] font-medium text-default">
          <span class="size-1.5 shrink-0 rounded-full" :class="dotClass[row.state]" aria-hidden="true" />
          <span class="max-w-[14ch] truncate">{{ row.snapshot?.characterName || row.user.displayName }}</span>
        </span>
        <span class="mt-0.5 flex items-center gap-2 text-[12px] text-darkest">
          <LiveValue
            v-if="row.snapshot"
            :value="row.snapshot.silverPerHour"
            :text="`${formatSilver(row.snapshot.silverPerHour)}/h`"
            class="font-medium text-yellow"
          />
          <span v-else>No session</span>
          <span v-if="row.snapshot && row.snapshot.deathCount > 0" class="text-accent">{{ row.snapshot.deathCount }} deaths</span>
        </span>
      </li>
    </ul>

    <!-- Mobile: the whole strip collapses to one tappable summary line. -->
    <RouterLink to="/party" class="flex min-w-0 flex-1 items-center gap-3 lg:hidden">
      <span class="min-w-0 flex-1 truncate text-[13px] text-darker">{{ statusLabel }}</span>
      <LiveValue
        :value="party.totals.value.silverPerHour"
        :text="`${formatSilver(party.totals.value.silverPerHour)}/h`"
        class="text-[13px] font-semibold text-yellow"
      />
      <Icon name="chevron" class="size-4 shrink-0 -rotate-90 text-darkest" />
    </RouterLink>

    <div class="hidden shrink-0 items-center gap-5 lg:flex">
      <div class="flex flex-col justify-center text-right">
        <span class="text-[10px] font-medium uppercase tracking-[0.1em] text-darkest">Party total</span>
        <LiveValue
          :value="party.totals.value.silverPerHour"
          :text="`${formatSilver(party.totals.value.silverPerHour)}/h`"
          class="mt-0.5 text-[15px] font-semibold text-yellow"
        />
      </div>
      <RouterLink
        to="/party"
        class="flex h-9 items-center gap-1.5 rounded-md border border-card bg-raised px-3 text-[13px] font-medium text-dark transition-colors duration-150 hover:border-light hover:text-default"
      >
        Open board
        <Icon name="arrowRight" class="size-3.5" />
      </RouterLink>
    </div>
  </section>
</template>
